<template>
    <div>
        <p v-if="!lineItems.length" class="text-center mt-5">No order items found.</p>

        <div v-if="lineItems.length" class="d-flex justify-content-between align-items-center line-item-tabs">
            <ul class="line-item-segments">
                <li v-for="item in lineItems" :key="item.id">
                    <a :class="['line-item-segment', { active: activeLineItemId === item.id }]"
                       href="javascript:void(0)" @click="activeLineItemId = item.id">
                        {{ item.product_name }}
                        <span class="segment-count">{{ costingsOf(item).length }}</span>
                    </a>
                </li>
            </ul>
            <button type="button" class="btn btn-primary btn-sm" @click="onClickAddNew">
                + Add New Costing
            </button>
        </div>

        <p v-if="lineItems.length && !activeCostings.length" class="text-center mt-5">No costing records for this item yet.</p>

        <div v-if="activeCostings.length" class="product-costing-tabs">
            <ul class="costing-pill-tabs">
                <li v-for="costing in activeCostings" :key="costing.id">
                    <a :class="['costing-pill', { active: activeCostingId === costing.id }]"
                       href="javascript:void(0)" @click="activeCostingId = costing.id">
                        {{ costingLabelOf(costing) }}
                    </a>
                </li>
            </ul>
        </div>

        <div v-if="activeCosting" :key="activeCosting.id" class="card general-card">
            <div class="card-body py-1 px-1">
                <div class="row">
                    <div class="col-md-4 col-12">
                        <p class="mb-25">Bill No: {{ activeCosting.bill_number }}</p>
                        <p class="mb-0">Costing Name: {{ activeCosting.product_costing_name }}</p>
                    </div>
                    <div class="col-md-4 col-12">
                        <p class="mb-25">Date: {{ readableDate(activeCosting.date) }}</p>
                        <p class="mb-0">Currency: {{ activeCosting.currency }}</p>
                    </div>
                    <div class="col-md-4 col-12 d-flex justify-content-between align-items-start">
                        <div>
                            <p class="mb-25">BOM Cost: {{ commaFormat(activeCosting.bom_cost) }}</p>
                            <p class="mb-25">Total Sales Price: {{ commaFormat(activeCosting.total_sales_price) }}</p>
                        </div>
                        <div class="d-flex gap-1">
                            <button type="button" class="btn btn-outline-secondary btn-sm" @click="onClickPrint(activeCosting)">
                                <i class="fas fa-print"></i>
                            </button>
                            <button type="button" class="btn btn-primary btn-sm" @click="onEdit(activeCosting)">Edit</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="px-1 pb-1">
                <CostingSectionCard
                    v-for="section in sectionsOf(activeCosting)"
                    :key="section.key"
                    :title="section.title"
                    :rows="section.rows"
                    :total="section.total"
                />

                <div class="card border-1 mb-1">
                    <div class="table-responsive">
                        <table class="table custom-table-padding mb-0">
                            <tbody>
                                <tr class="bg-light-success">
                                    <th>Prime Cost (A+B+C)</th>
                                    <th class="text-right">{{ commaFormat(primeCostOf(activeCosting)) }}</th>
                                </tr>
                                <tr>
                                    <th>Total Manufacturing Cost (A+B+C+D)</th>
                                    <th class="text-right">{{ commaFormat(totalManufacturingCostOf(activeCosting)) }}</th>
                                </tr>
                                <tr class="bg-light-primary">
                                    <th>E. General &amp; Admin Overheads</th>
                                    <th class="text-right">{{ commaFormat(activeCosting.general_admin_overheads) }}</th>
                                </tr>
                                <tr class="bg-light-success">
                                    <th>Total Cost (A+B+C+D+E)</th>
                                    <th class="text-right">{{ commaFormat(totalCostOf(activeCosting)) }}</th>
                                </tr>
                                <tr>
                                    <th>Markup %</th>
                                    <th class="text-right">{{ activeCosting.markup }} % ({{ commaFormat(markupAmountOf(activeCosting)) }})</th>
                                </tr>
                                <tr class="bg-light-primary">
                                    <th>Sales Price without VAT</th>
                                    <th class="text-right">{{ commaFormat(salePriceWithoutVatOf(activeCosting)) }}</th>
                                </tr>
                                <tr>
                                    <th>VAT</th>
                                    <th class="text-right">{{ activeCosting.vat }} % ({{ commaFormat(vatAmountOf(activeCosting)) }})</th>
                                </tr>
                                <tr class="bg-light-primary">
                                    <th>Sales Price including VAT</th>
                                    <th class="text-right">{{ commaFormat(salePriceIncludingVatOf(activeCosting)) }}</th>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <Loader v-if="printing"/>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import figureFormatter from '@/services/utils/figureFormatter'
import useDate from '@/services/utils/day'
import handlePurchase from '@/services/modules/purchase'
import handleBarcode from '@/services/modules/barcode'
import handleCompany from '@/services/modules/company'
import productCostingPDF from '@/services/utils/pdf/productCosting'
import CostingSectionCard from './CostingSectionCard.vue'
import Loader from '@/components/atom/LoaderComponent'

const props = defineProps({
    orderDetail: { type: Object, required: true },
    onCreateNew: { type: Function, required: true },
    onEdit: { type: Function, required: true }
})

const route = useRoute()
const store = useStore()
const { commaFormat } = figureFormatter()
const { formatToDdMmYy, formatDateTime } = useDate()
const { fetchSingleProductCosting } = handlePurchase()
const { fetchBarcode } = handleBarcode()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF: exportCostingPDF } = productCostingPDF()

const printing = ref(false)
const companyInfo = ref({})

const SECTION_DEFS = [
    { key: 'main_product', title: 'Main Product', match: (g) => g.type === 'production' && g.product_type === 'main_product' },
    { key: 'by_product', title: 'By Product', match: (g) => g.type === 'production' && g.product_type !== 'main_product' },
    { key: 'raw_materials', title: 'A. Raw Materials', match: (g) => g.type === 'raw_materials' },
    { key: 'packing_materials', title: 'B. Packing Materials', match: (g) => g.type === 'packing_materials' },
    { key: 'direct_labor', title: 'C. Direct Labor', match: (g) => g.type === 'direct_labor' },
    { key: 'direct_overhead', title: 'D. Direct Overhead', match: (g) => g.type === 'direct_overhead' }
]

const costingList = computed(() => {
    if (!props.orderDetail.costing) return []
    return props.orderDetail.costing
})

const lineItems = computed(() => {
    if (!props.orderDetail.order_summary) return []
    if (!props.orderDetail.order_summary.line_items) return []
    return props.orderDetail.order_summary.line_items
})

const costingsByLineItemId = computed(() => {
    const map = {}
    costingList.value.forEach((costing) => {
        if (!costing.product_costingable_id) return
        if (!map[costing.product_costingable_id]) map[costing.product_costingable_id] = []
        map[costing.product_costingable_id].push(costing)
    })
    return map
})

const costingsOf = (item) => {
    if (!costingsByLineItemId.value[item.id]) return []
    return costingsByLineItemId.value[item.id]
}

const activeLineItemId = ref(null)
const activeCostingId = ref(null)

const activeLineItem = computed(() => {
    return lineItems.value.find((item) => item.id === activeLineItemId.value) || null
})

const activeCostings = computed(() => {
    if (!activeLineItem.value) return []
    return costingsOf(activeLineItem.value)
})

watch(lineItems, (items) => {
    if (!items.length) return activeLineItemId.value = null
    const stillExists = items.some((item) => item.id === activeLineItemId.value)
    if (stillExists) return
    activeLineItemId.value = items[0].id
}, { immediate: true })

watch(activeCostings, (list) => {
    if (!list.length) return activeCostingId.value = null
    const stillExists = list.some((costing) => costing.id === activeCostingId.value)
    if (stillExists) return
    activeCostingId.value = list[0].id
}, { immediate: true })

const activeCosting = computed(() => {
    return activeCostings.value.find((costing) => costing.id === activeCostingId.value) || null
})

const onClickAddNew = () => {
    if (!activeLineItem.value) return
    props.onCreateNew({ id: activeLineItem.value.id }, 'costing')
}

const generalsOf = (costing) => {
    if (!costing.general) return []
    return costing.general
}

const costingLabelOf = (costing) => {
    if (!costing.product_costing_name) return costing.bill_number
    return costing.product_costing_name
}

const sumAmount = (rows) => {
    return rows.reduce((total, row) => total + parseFloat(row.amount || 0), 0)
}

const sectionsOf = (costing) => {
    const generals = generalsOf(costing)
    return SECTION_DEFS.map((def) => {
        const rows = generals.filter(def.match)
        return { key: def.key, title: def.title, rows, total: sumAmount(rows) }
    }).filter((section) => section.rows.length)
}

const amountByType = (costing, type) => {
    const rows = generalsOf(costing).filter((g) => g.type === type)
    return sumAmount(rows)
}

const primeCostOf = (costing) => {
    return amountByType(costing, 'raw_materials') + amountByType(costing, 'packing_materials') + amountByType(costing, 'direct_labor')
}

const totalManufacturingCostOf = (costing) => {
    return primeCostOf(costing) + amountByType(costing, 'direct_overhead')
}

const totalCostOf = (costing) => {
    return totalManufacturingCostOf(costing) + parseFloat(costing.general_admin_overheads || 0)
}

const markupAmountOf = (costing) => {
    return (totalCostOf(costing) * parseFloat(costing.markup || 0)) / 100
}

const salePriceWithoutVatOf = (costing) => {
    return totalCostOf(costing) + markupAmountOf(costing)
}

const vatAmountOf = (costing) => {
    return (salePriceWithoutVatOf(costing) * parseFloat(costing.vat || 0)) / 100
}

const salePriceIncludingVatOf = (costing) => {
    return salePriceWithoutVatOf(costing) + vatAmountOf(costing)
}

const readableDate = (date) => {
    if (!date) return ''
    return formatToDdMmYy(String(date).split(' ')[0])
}

const companyQuery = () => {
    return `?company_id=${route.params.companyId}`
}

const buildQrCodeQuery = (billNumber, refDate, userName) => {
    const qrcodeData = JSON.stringify({
        ref_no: billNumber,
        ref_date: refDate,
        created_by: userName,
        created_date_time: formatDateTime(new Date())
    })
    return `?code=${billNumber}&qrcode=${qrcodeData}`
}

const loadCompanyInfo = async () => {
    if (companyInfo.value.id) return
    await fetchCompanyInfo(route.params.companyId).then((res) => {
        companyInfo.value = res.data
    })
}

const onClickPrint = async (costing) => {
    if (!costing.id) return
    const user = store.state.user.user
    printing.value = true
    await loadCompanyInfo()

    await fetchSingleProductCosting(costing.id, companyQuery()).then(async ({ status, data }) => {
        if (!status) return
        const barcodeQuery = buildQrCodeQuery(data.bill_number, data.date, user.name)
        await fetchBarcode(barcodeQuery).then((res) => {
            exportCostingPDF(companyInfo.value, data, res.barcode, res.qrcode, user.name)
        })
    })

    printing.value = false
}
</script>

<style scoped>
.general-card {
    min-height: auto;
}
.line-item-tabs {
    margin-bottom: 1rem;
    gap: 1rem;
    flex-wrap: wrap;
}
.line-item-segments {
    display: inline-flex;
    flex-wrap: wrap;
    padding: 4px;
    margin: 0;
    list-style: none;
    background-color: #f3f2f7;
    border: 1px solid #e6e4ee;
    border-radius: 8px;
}
.line-item-segment {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border-radius: 6px;
    color: #6e6b7b;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
}
.line-item-segment:hover {
    color: #2f2b3d;
}
.line-item-segment.active {
    background-color: #ffffff;
    color: #7367f0;
    box-shadow: 0 1px 3px rgba(34, 41, 47, 0.12);
}
.segment-count {
    padding: 0 6px;
    border-radius: 10px;
    background-color: #e4e2ec;
    color: #6e6b7b;
    font-size: 11px;
    line-height: 18px;
}
.line-item-segment.active .segment-count {
    background-color: rgba(115, 103, 240, 0.14);
    color: #7367f0;
}
.product-costing-tabs {
    margin-bottom: 1rem;
}
.costing-pill-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
}
.costing-pill {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 20px;
    background-color: #f8f8fb;
    border: 1px solid #eeedf2;
    color: #6e6b7b;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
}
.costing-pill:hover {
    background-color: #eeedf2;
    color: #2f2b3d;
}
.costing-pill.active {
    background-color: #7367f0;
    border-color: #7367f0;
    color: #ffffff;
}
</style>
